import * as React from 'react';
import { createContext, useState } from 'react';

import type { SharedLearnerProfile_PortfolioProjectsFragment as LearnerProfileProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import type { SharedPortfolioProject_UserPortfolioProjectFragment as UserPortfolioProject } from 'bundles/portfolio-projects/queries/__generated__/SharedPortfolioProject_UserPortfolioProject';
import type { Nullable } from 'bundles/portfolio-projects/types';
import { mapLearnerProfileProjectToUserProject } from 'bundles/portfolio-projects/utils/typeMappers';

// Mutually exclusive keys
// https://juhanajauhiainen.com/posts/how-to-type-an-object-with-exclusive-or-properties-in-typescript
type Project = { project: Nullable<LearnerProfileProject>; projectsList: never };
type ProjectsList = { projectsList: LearnerProfileProject[]; project: never };
type PublishedStatus = { isPublished: boolean };
type PortfolioBuilderConfig = PublishedStatus & Partial<Project | ProjectsList>;

type PortfolioBuilderContextType = {
  isPortfolioBuilderOpen: boolean;
  selectedProject: Nullable<UserPortfolioProject>;
  projectsList: UserPortfolioProject[];
  openPortfolioBuilder: (config: PortfolioBuilderConfig) => void;
  closePortfolioBuilder: () => void;
  updatePortfolioProject: (project: UserPortfolioProject) => void;
};

export const PortfolioBuilderContext = createContext<PortfolioBuilderContextType>({
  isPortfolioBuilderOpen: false,
  selectedProject: null,
  projectsList: [],
  openPortfolioBuilder: () => undefined,
  closePortfolioBuilder: () => undefined,
  updatePortfolioProject: () => undefined,
});

export const PortfolioBuilderContextProvider: React.FC = ({ children }) => {
  const [isPortfolioBuilderOpen, setIsPortfolioBuilderOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Nullable<UserPortfolioProject>>(null);
  const [projectsList, setProjectsList] = useState<UserPortfolioProject[]>([]);

  const openPortfolioBuilder = (config: PortfolioBuilderConfig = { isPublished: false }) => {
    setIsPortfolioBuilderOpen(true);
    setSelectedProject(
      mapLearnerProfileProjectToUserProject(config.project || null, { isPublished: config.isPublished })
    );
    setProjectsList(
      (config.projectsList || [])
        .map((p) => mapLearnerProfileProjectToUserProject(p, { isPublished: config.isPublished }))
        .filter((v) => v !== null) as UserPortfolioProject[]
    );
  };

  const closePortfolioBuilder = () => {
    setIsPortfolioBuilderOpen(false);
    setSelectedProject(null);
    setProjectsList([]);
  };

  const updatePortfolioProject = (project: UserPortfolioProject) => {
    if (selectedProject?.project.id === project.project.id) {
      setSelectedProject(project);
      return;
    }

    const projectToUpdate = projectsList.find((p) => p.project.id === project.project.id);
    if (projectToUpdate) {
      if (!projectToUpdate.isPublished && project.isPublished) {
        setProjectsList([]);
        setSelectedProject(project);
      } else if (!projectToUpdate.isPublished && !project.isPublished) {
        const index = projectsList.indexOf(projectToUpdate);
        const updatedProjectsList = [...projectsList.slice(0, index), project, ...projectsList.slice(index + 1)];
        setProjectsList(updatedProjectsList);
      }
    }
  };

  return (
    <PortfolioBuilderContext.Provider
      value={{
        isPortfolioBuilderOpen,
        selectedProject,
        projectsList,
        openPortfolioBuilder,
        closePortfolioBuilder,
        updatePortfolioProject,
      }}
    >
      {children}
    </PortfolioBuilderContext.Provider>
  );
};
